// The boat's equipment on the web (TOM-409, cloud ADR 0312): the read, the
// owner's answer, and the words the Equipment page and a board item's "Not on
// this boat" use, over the routes TOM-407 built (api_contracts §5.18).
//
// An answer is a fact about the boat, shared by its owners: "Not on this boat"
// leaves an engine, gearbox, tank or battery out of the boards, the pickers and
// Neo for everyone aboard; its data stays, and its alarms keep running. The
// cloud filters every reading the web is sent, so the web never matches a path
// to an instance by a rule of its own: it uses each instance's `paths`.
//
// The make and model the owner gives for the boat's own gear (cloud ADR 0425,
// TOM-878) ride the same list: on each engine, gearbox and battery, and in
// `gear` for the generators, chargers and inverters.

import {
  EQUIPMENT_FAMILY_ORDER,
  GEAR_FAMILY_ORDER,
  type EquipmentEntry,
  type EquipmentFamily,
  type EquipmentList,
  type GearEntry,
  type GearFamily,
  type GearMakeModel,
  type MakeModelAnswerState,
  type PresenceAnswer,
  type PresenceAnswerState,
} from '@neomaris/protocol';

import { api } from '../api';

export type { EquipmentEntry, EquipmentFamily, GearEntry, GearMakeModel, PresenceAnswer };

// ----- Reading ---------------------------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const FAMILIES = new Set<string>(EQUIPMENT_FAMILY_ORDER);

function parsePresence(raw: unknown): PresenceAnswerState | null {
  if (!isObject(raw)) return null;
  const { answer, origin, answeredBy, answeredAt, version } = raw;
  if (answer !== null && answer !== 'not_on_boat' && answer !== 'real') return null;
  if (origin !== 'owner' && origin !== 'neo') return null;
  if (typeof answeredAt !== 'string') return null;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;
  return {
    answer,
    origin,
    answeredBy: typeof answeredBy === 'string' ? answeredBy : null,
    answeredAt,
    version,
  };
}

function parseMakeModel(raw: unknown): MakeModelAnswerState | null {
  if (!isObject(raw)) return null;
  const { answer, origin, answeredBy, answeredAt, version } = raw;
  let words: GearMakeModel | null = null;
  if (isObject(answer)) {
    if (typeof answer.make !== 'string' || typeof answer.model !== 'string') return null;
    words = { make: answer.make, model: answer.model };
  } else if (answer !== null) return null;
  if (origin !== 'owner' && origin !== 'neo') return null;
  if (typeof answeredAt !== 'string') return null;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;
  return {
    answer: words,
    origin,
    answeredBy: typeof answeredBy === 'string' ? answeredBy : null,
    answeredAt,
    version,
  };
}

const GEAR_FAMILIES = new Set<string>(GEAR_FAMILY_ORDER);

/** One entry of the list's `gear`, or null when it is not one. */
export function parseGearEntry(raw: unknown): GearEntry | null {
  if (!isObject(raw)) return null;
  const { instance, family, label, reporting } = raw;
  if (typeof instance !== 'string' || instance === '') return null;
  if (typeof family !== 'string' || !GEAR_FAMILIES.has(family)) return null;
  if (typeof label !== 'string') return null;
  return {
    instance,
    family: family as GearFamily,
    label,
    reporting: reporting === true,
    makeModel: parseMakeModel(raw.makeModel),
  };
}

/** One entry of `GET …/equipment`, or null when it is not one. */
export function parseEquipmentEntry(raw: unknown): EquipmentEntry | null {
  if (!isObject(raw)) return null;
  const { instance, family, label, state, reason, question, paths } = raw;
  if (typeof instance !== 'string' || instance === '') return null;
  if (typeof family !== 'string' || !FAMILIES.has(family)) return null;
  if (typeof label !== 'string') return null;
  if (state !== 'shown' && state !== 'left_out') return null;
  if (!Array.isArray(paths) || paths.some((p) => typeof p !== 'string')) return null;
  const evidence = isObject(raw.evidence)
    ? {
        ...(Array.isArray(raw.evidence.phantomReasons)
          ? {
              phantomReasons: raw.evidence.phantomReasons.filter(
                (r): r is string => typeof r === 'string'
              ),
            }
          : {}),
        ...(typeof raw.evidence.aliasOf === 'string' ? { aliasOf: raw.evidence.aliasOf } : {}),
      }
    : null;
  const answers = isObject(raw.answers) ? raw.answers : {};
  const alarms = Array.isArray(raw.alarms)
    ? raw.alarms.flatMap((a) =>
        isObject(a) && typeof a.id === 'string' && typeof a.title === 'string'
          ? [{ id: a.id, title: a.title }]
          : []
      )
    : [];
  return {
    instance,
    family: family as EquipmentFamily,
    label,
    state,
    reason: reason === 'owner' || reason === 'phantom' || reason === 'unreadable' ? reason : null,
    question:
      question === 'phantom' ||
      question === 'alias' ||
      question === 'unreadable' ||
      question === 'other_engine' ||
      question === 'gearbox_of_removed_engine'
        ? question
        : null,
    evidence,
    answers: {
      presence: parsePresence(answers.presence),
      ...('makeModel' in answers ? { makeModel: parseMakeModel(answers.makeModel) } : {}),
    },
    paths: paths as string[],
    lastSeen: typeof raw.lastSeen === 'string' ? raw.lastSeen : null,
    pinnedElsewhere: typeof raw.pinnedElsewhere === 'number' ? raw.pinnedElsewhere : 0,
    alarms,
  };
}

/** `GET …/equipment`, or null when the body is not one. An entry it cannot read is skipped. */
export function parseEquipmentList(raw: unknown): EquipmentList | null {
  if (!isObject(raw) || !Array.isArray(raw.instances)) return null;
  return {
    instances: raw.instances.flatMap((e) => {
      const entry = parseEquipmentEntry(e);
      return entry === null ? [] : [entry];
    }),
    gear: (Array.isArray(raw.gear) ? raw.gear : []).flatMap((g) => {
      const entry = parseGearEntry(g);
      return entry === null ? [] : [entry];
    }),
  };
}

export async function fetchEquipment(boatId: string): Promise<EquipmentList> {
  const list = parseEquipmentList(await api(`/v1/boats/${boatId}/equipment`));
  if (list === null) throw new Error('equipment: unexpected body');
  return list;
}

// ----- Answering -------------------------------------------------------------------

export type AnswerResult =
  | { kind: 'saved'; entry: EquipmentEntry }
  /** `409 version_conflict`: someone answered first; read the list again. */
  | { kind: 'conflict' }
  | { kind: 'failed' };

/** The version an answer is written against: the instance's, or 0 when it has none. */
export function answerVersion(entry: EquipmentEntry): number {
  return entry.answers.presence?.version ?? 0;
}

/**
 * Answer about one instance, or clear the answer with null. The idempotency key
 * belongs to this answer: a retry of it sends the same key.
 */
export async function answerEquipment(
  boatId: string,
  instance: string,
  answer: PresenceAnswer | null,
  expectedVersion: number,
  idempotencyKey: string
): Promise<AnswerResult> {
  try {
    const raw = await api(`/v1/boats/${boatId}/equipment/${encodeURIComponent(instance)}`, {
      method: 'PUT',
      body: { kind: 'presence', answer, expectedVersion },
      idempotencyKey,
    });
    const entry = parseEquipmentEntry(raw);
    return entry === null ? { kind: 'failed' } : { kind: 'saved', entry };
  } catch (err) {
    const status = isObject(err) && typeof err.status === 'number' ? err.status : null;
    return status === 409 ? { kind: 'conflict' } : { kind: 'failed' };
  }
}

/**
 * Save the make and model of one piece of gear, or forget them with null
 * (cloud ADR 0425). The page reads the list again after it.
 */
export async function answerMakeModel(
  boatId: string,
  instance: string,
  answer: GearMakeModel | null,
  expectedVersion: number,
  idempotencyKey: string
): Promise<{ kind: 'saved' } | { kind: 'conflict' } | { kind: 'failed' }> {
  try {
    await api(`/v1/boats/${boatId}/equipment/${encodeURIComponent(instance)}`, {
      method: 'PUT',
      body: { kind: 'make_model', answer, expectedVersion },
      idempotencyKey,
    });
    return { kind: 'saved' };
  } catch (err) {
    const status = isObject(err) && typeof err.status === 'number' ? err.status : null;
    return status === 409 ? { kind: 'conflict' } : { kind: 'failed' };
  }
}

/** "Yanmar 4JH45", or null when the owner has not given one. */
export function makeModelText(state: MakeModelAnswerState | null | undefined): string | null {
  const answer = state?.answer ?? null;
  return answer === null ? null : `${answer.make} ${answer.model}`;
}

const GEAR_NOUN: Readonly<Record<GearFamily, string>> = {
  engine: 'Engine',
  gearbox: 'Gearbox',
  generator: 'Generator',
  battery: 'Battery',
  charger: 'Charger',
  inverter: 'Inverter',
};

/** One piece of gear's family word, on its row: "Generator". */
export function gearNoun(family: GearFamily): string {
  return GEAR_NOUN[family];
}

// ----- Matching a board item ------------------------------------------------------------

/**
 * The one instance all of an item's paths belong to, by the list's exact paths,
 * or null when they belong to none, or to more than one. Only then does a board
 * item offer "Not on this boat".
 */
export function instanceOfPaths(
  paths: readonly string[],
  list: readonly EquipmentEntry[]
): EquipmentEntry | null {
  if (paths.length === 0) return null;
  const owner = list.find((e) => e.paths.includes(paths[0]!));
  if (owner === undefined) return null;
  return paths.every((p) => owner.paths.includes(p)) ? owner : null;
}

// ----- Words --------------------------------------------------------------------------

export const EQUIPMENT_TITLE = 'Equipment';

const FAMILY_HEADING: Readonly<Record<EquipmentFamily, string>> = {
  engine: 'Engines',
  gearbox: 'Gearboxes',
  tank: 'Tanks',
  battery: 'Batteries',
};

const FAMILY_NOUN: Readonly<Record<EquipmentFamily, string>> = {
  engine: 'engine',
  gearbox: 'gearbox',
  tank: 'tank',
  battery: 'battery',
};

export function familyHeading(family: EquipmentFamily): string {
  return FAMILY_HEADING[family];
}

/** The label of another instance on the list, by its key, or the key itself. */
function labelOf(instance: string, list: readonly EquipmentEntry[]): string {
  return list.find((e) => e.instance === instance)?.label ?? instance;
}

/**
 * Whether an instance is drawn, and why, in a line under its name (ADR 0312
 * §8). An owner's answer says who gave it: the owner, or Neo on the owner's
 * words.
 */
export function statusText(entry: EquipmentEntry): string {
  const presence = entry.answers.presence;
  if (entry.reason === 'owner' && presence !== null && presence.answer !== null) {
    const answer = presence.answer === 'real' ? 'On this boat' : 'Not on this boat';
    return presence.origin === 'neo'
      ? `${answer}: an owner’s answer, through Neo`
      : `${answer}: an owner’s answer`;
  }
  if (entry.reason === 'phantom') {
    return 'Left out: Neomaris has never seen it run, so it may be a misreading';
  }
  if (entry.reason === 'unreadable') {
    return `Left out: Neomaris can’t tell which ${FAMILY_NOUN[entry.family]} this is`;
  }
  return 'Shown';
}

/**
 * The open question, in the words the page asks it (ADR 0312 §2, §9), or null.
 * Neomaris asks; it never settles one answer from another.
 */
export function questionText(
  entry: EquipmentEntry,
  list: readonly EquipmentEntry[]
): string | null {
  const noun = FAMILY_NOUN[entry.family];
  switch (entry.question) {
    case 'phantom':
      return `Neomaris has never seen this ${noun} run. Is it on this boat?`;
    case 'alias': {
      const other = entry.evidence?.aliasOf;
      return other === undefined
        ? `This may be another engine under another number. Is it on this boat?`
        : `This may be the ${labelOf(other, list)} under another number. Is there a separate ${entry.label.toLowerCase()}?`;
    }
    case 'unreadable':
      return `Neomaris can’t tell which ${noun} this is. Is it one of your ${FAMILY_HEADING[entry.family].toLowerCase()}?`;
    case 'other_engine':
      return 'Another engine is confirmed. Is this one on the boat too?';
    case 'gearbox_of_removed_engine': {
      const engine = labelOf(entry.instance.replace(/\.transmission$/, ''), list);
      return `${engine} is not on this boat, but this gearbox still reports. Is it on this boat?`;
    }
    default:
      return null;
  }
}

/** "3 readings", "1 reading". */
export function readingsCount(entry: EquipmentEntry): string {
  const n = entry.paths.length;
  return `${n} ${n === 1 ? 'reading' : 'readings'}`;
}

/**
 * The page's order: open questions first, then each family in turn (engines,
 * gearboxes, tanks, batteries), each in the list's own order.
 */
export function equipmentSections(list: readonly EquipmentEntry[]): Array<{
  key: 'questions' | EquipmentFamily;
  heading: string;
  entries: EquipmentEntry[];
}> {
  const questions = list.filter((e) => e.question !== null);
  const sections: Array<{
    key: 'questions' | EquipmentFamily;
    heading: string;
    entries: EquipmentEntry[];
  }> = [];
  if (questions.length > 0) {
    sections.push({ key: 'questions', heading: 'Questions for you', entries: questions });
  }
  for (const family of EQUIPMENT_FAMILY_ORDER) {
    const entries = list.filter((e) => e.family === family && e.question === null);
    if (entries.length > 0)
      sections.push({ key: family, heading: FAMILY_HEADING[family], entries });
  }
  return sections;
}

/** What "Not on this boat" will do, said before it is done (the board item's confirmation). */
export function notOnBoatConsequence(entry: EquipmentEntry, boatName: string): string {
  const alarms =
    entry.alarms.length === 0
      ? ''
      : ` ${entry.alarms.length === 1 ? 'Its alarm keeps' : `Its ${entry.alarms.length} alarms keep`} running.`;
  return `It leaves every ${boatName} board, the reading pickers and Neo, for every owner. Its data stays, and you can put it back.${alarms}`;
}

/** The paths a board item reads: an instrument's or a chart's, or none. */
export function boardItemPaths(spec: unknown): string[] {
  if (!isObject(spec) || !Array.isArray(spec.paths)) return [];
  return spec.paths.filter((p): p is string => typeof p === 'string');
}
